import type {
    InstructionStepDto,
    LessonDetailDto,
    StepMaterialDto,
    StepPlannedFigureDto,
    StepRasterFigureDto,
    RasterOptionDto,
    StepReadingDto,
    StepVideoDto,
    StepWorksheetSectionDto,
    SupportingMaterialDto,
    UnitDto
} from '../../InProgressUnitsPanel/types';
import { normalizeFigureKey, normalizeIdPart } from '../../InProgressUnitsPanel/loadingStateKeys';
import { resolveStepIdForMediaCommand } from '../../InProgressUnitsPanel/mediaReviewUtils';

/** Match FIGURE_DIAGRAM host markers in reading/worksheet markdown (canonical Fig. A casing). */
function contentHostsFigureKey(content: string | undefined | null, figureKey: string): boolean {
    if (!content) return false;
    const needle = `key=${normalizeFigureKey(figureKey)}`.toLowerCase();
    return content.toLowerCase().includes(needle);
}

/** Stable key for video approval / override state (unit + lesson + step). */
export function videoApprovalStateKey(
    unitId: unknown,
    lessonId: unknown,
    materialId: unknown
): string {
    const resolvedMaterialId =
        resolveStepIdForMediaCommand(materialId == null ? undefined : String(materialId)) ?? materialId;
    return `${normalizeIdPart(unitId)}|${normalizeIdPart(lessonId)}|${normalizeIdPart(resolvedMaterialId)}`;
}

/** Lesson-scoped key for waiting on C322 save completion before C323 approve. */
export function lessonVideoSaveWaiterKey(unitId: unknown, lessonId: unknown): string {
    return `${normalizeIdPart(unitId)}|${normalizeIdPart(lessonId)}`;
}

/** Api bus JSON may send enumeration as a plain key string or as `{ key, value }` / `{ Key, Value }`. */
function pickEnumerationKey(raw: unknown): string | undefined {
    if (raw == null) return undefined;
    if (typeof raw === 'string') {
        const t = raw.trim();
        return t === '' ? undefined : t;
    }
    if (typeof raw === 'object') {
        const o = raw as Record<string, unknown>;
        const k = o.key ?? o.Key;
        if (k != null) {
            const s = String(k).trim();
            return s === '' ? undefined : s;
        }
    }
    return undefined;
}

function mapWirePriorDeliveryGuidanceList(
    raw: unknown
): NonNullable<LessonDetailDto['e251_PriorDeliveryGuidance']> {
    if (!Array.isArray(raw)) return [];
    return raw
        .map(row => {
            const r = row as Record<string, unknown>;
            const pick = (a: string, b: string) => pickWireField(r, a, b);
            const guidance = pick('e251_Guidance', 'E251_Guidance');
            if (guidance == null || String(guidance).trim() === '') return null;
            const sessionRaw = pickWireField(
                r,
                'e251_PriorSessionId',
                'E251_PriorSessionId',
                'e251_SessionId',
                'E251_SessionId'
            );
            const completedAt = pickWireField(
                r,
                'e251_PriorCompletedAt',
                'E251_PriorCompletedAt',
                'e251_CompletedAt',
                'E251_CompletedAt'
            );
            return {
                e251_PriorSessionId:
                    sessionRaw == null || sessionRaw === '' ? undefined : Number(sessionRaw),
                e251_Guidance: String(guidance),
                e251_PriorCompletedAt:
                    completedAt == null || completedAt === ''
                        ? undefined
                        : completedAt === null
                          ? null
                          : String(completedAt)
            };
        })
        .filter((entry): entry is NonNullable<typeof entry> => entry != null);
}

function pickWireField(
    row: Record<string, unknown> | null | undefined,
    ...keys: string[]
): unknown {
    if (row == null || typeof row !== 'object') return undefined;
    for (const key of keys) {
        if (!(key in row)) continue;
        return (row as Record<string, unknown>)[key];
    }
    return undefined;
}

function mapWireStepPlannedFigures(raw: unknown): StepPlannedFigureDto[] | undefined {
    if (!Array.isArray(raw)) return undefined;
    return raw.map(row => {
        const r = row as Record<string, unknown>;
        return {
            e251_Key: pickWireField(r, 'e251_Key', 'E251_Key') as string | undefined,
            e251_FigurePurpose: pickWireField(
                r,
                'e251_FigurePurpose',
                'E251_FigurePurpose',
                'e251_Purpose',
                'E251_Purpose'
            ) as string | undefined,
            e251_FigureType: pickWireField(
                r,
                'e251_FigureType',
                'E251_FigureType',
                'e251_ImageType',
                'E251_ImageType'
            ) as string | undefined,
            e251_Role: pickWireField(r, 'e251_Role', 'E251_Role') as string | undefined
        };
    });
}

function mapWireStepMaterials(raw: unknown): StepMaterialDto[] | undefined {
    if (!Array.isArray(raw)) return undefined;
    return raw.map(row => {
        const r = row as Record<string, unknown>;
        const pick = (a: string, b: string) => pickWireField(r, a, b);
        const fromStep = pick('e251_FromStepNumber', 'E251_FromStepNumber');
        return {
            e251_Kind: pick('e251_Kind', 'E251_Kind') as string | undefined,
            e251_Description: pick('e251_Description', 'E251_Description') as string | undefined,
            e251_FromStepNumber:
                fromStep == null || fromStep === '' ? undefined : Number(fromStep)
        };
    });
}

function mapWireStepReading(raw: unknown): StepReadingDto | undefined {
    if (raw == null || typeof raw !== 'object') return undefined;
    const r = raw as Record<string, unknown>;
    const content = pickWireField(
        r,
        'e251_ReadingContent',
        'E251_ReadingContent',
        'e251_Content',
        'E251_Content'
    ) as string | undefined;
    const title = pickWireField(
        r,
        'e251_ReadingTitle',
        'E251_ReadingTitle',
        'e251_Title',
        'E251_Title'
    ) as string | undefined;
    const purpose = pickWireField(
        r,
        'e251_ReadingPurpose',
        'E251_ReadingPurpose',
        'e251_Purpose',
        'E251_Purpose'
    ) as string | undefined;
    const readingKind = pickWireField(
        r,
        'e251_ReadingKind',
        'E251_ReadingKind'
    ) as string | undefined;
    const figures = mapWireStepPlannedFigures(
        pickWireField(r, 'e251_ReadingFigures', 'E251_ReadingFigures', 'e251_Figures', 'E251_Figures')
    );
    if (!content?.trim() && !title?.trim() && !purpose?.trim()) return undefined;
    return {
        e251_ReadingTitle: title,
        e251_ReadingPurpose: purpose,
        e251_ReadingKind: readingKind,
        e251_ReadingContent: content,
        e251_ReadingFigures: figures
    };
}

function mapWireStepWorksheetSection(raw: unknown): StepWorksheetSectionDto | undefined {
    if (raw == null || typeof raw !== 'object') return undefined;
    const r = raw as Record<string, unknown>;
    const content = pickWireField(
        r,
        'e251_WorksheetContent',
        'E251_WorksheetContent',
        'e251_Content',
        'E251_Content'
    ) as string | undefined;
    const letter = pickWireField(r, 'e251_Letter', 'E251_Letter') as string | undefined;
    const sectionPurpose = pickWireField(r, 'e251_SectionPurpose', 'E251_SectionPurpose') as string | undefined;
    const figures = mapWireStepPlannedFigures(
        pickWireField(r, 'e251_WorksheetFigures', 'E251_WorksheetFigures', 'e251_Figures', 'E251_Figures')
    );
    if (!content?.trim() && !letter?.trim() && !sectionPurpose?.trim()) return undefined;
    return {
        e251_Letter: letter,
        e251_SectionPurpose: sectionPurpose,
        e251_WorksheetContent: content,
        e251_WorksheetFigures: figures
    };
}

function mapWireRasterOptions(raw: unknown): RasterOptionDto[] | undefined {
    if (!Array.isArray(raw)) return undefined;
    return raw.map(row => {
        const r = row as Record<string, unknown>;
        const pick = (a: string, b: string) => pickWireField(r, a, b);
        const rank = pick('e251_Rank', 'E251_Rank') ?? pick('e435_Rank', 'E435_Rank');
        return {
            e251_OptionId: (pick('e251_OptionId', 'E251_OptionId') ??
                pick('e435_OptionId', 'E435_OptionId')) as string | undefined,
            e251_BlobId: (pick('e251_BlobId', 'E251_BlobId') ??
                pick('e435_BlobId', 'E435_BlobId')) as string | undefined,
            e251_ImageUrl: (pick('e251_ImageUrl', 'E251_ImageUrl') ??
                pick('e435_ImageUrl', 'E435_ImageUrl')) as string | undefined,
            e251_RasterOptionTitle: (pick('e251_RasterOptionTitle', 'E251_RasterOptionTitle') ??
                pick('e435_Title', 'E435_Title')) as string | undefined,
            e251_Source: (pick('e251_Source', 'E251_Source') ??
                pick('e435_Source', 'E435_Source')) as string | undefined,
            e251_Rank: rank == null ? undefined : Number(rank),
        };
    });
}

function mapWireRasterFigures(raw: unknown): StepRasterFigureDto[] | undefined {
    if (!Array.isArray(raw)) return undefined;
    return raw.map(row => {
        const r = row as Record<string, unknown>;
        const pick = (a: string, b: string) => pickWireField(r, a, b);
        const approved = pick('e251_RasterApproved', 'E251_RasterApproved');
        const textOnlyAccepted = pick('e251_TextOnlyAccepted', 'E251_TextOnlyAccepted');
        return {
            e251_FigureKey: pick('e251_FigureKey', 'E251_FigureKey') as string | undefined,
            e251_GenerationState: pick('e251_GenerationState', 'E251_GenerationState') as
                | string
                | undefined,
            e251_ImageUrl: pick('e251_ImageUrl', 'E251_ImageUrl') as string | undefined,
            e251_RasterTitle: pick('e251_RasterTitle', 'E251_RasterTitle') as string | undefined,
            e251_GenerationError: pick('e251_GenerationError', 'E251_GenerationError') as
                | string
                | undefined,
            e251_RasterApproved:
                approved === true || approved === 'true'
                    ? true
                    : approved === false || approved === 'false'
                      ? false
                      : undefined,
            e251_TextOnlyAccepted:
                textOnlyAccepted === true || textOnlyAccepted === 'true'
                    ? true
                    : textOnlyAccepted === false || textOnlyAccepted === 'false'
                      ? false
                      : undefined,
            e251_SelectedOptionId: pick('e251_SelectedOptionId', 'E251_SelectedOptionId') as
                | string
                | null
                | undefined,
            e251_FigureType: pickWireField(
                r,
                'e251_FigureType',
                'E251_FigureType',
                'e251_ImageType',
                'E251_ImageType'
            ) as string | undefined,
            e251_FigurePurpose: pick('e251_FigurePurpose', 'E251_FigurePurpose') as
                | string
                | undefined,
            e251_Role: pick('e251_Role', 'E251_Role') as string | undefined,
            e251_ImageSearchQuery: pick('e251_ImageSearchQuery', 'E251_ImageSearchQuery') as
                | string
                | undefined,
            e251_ImageSearchUrl: pick('e251_ImageSearchUrl', 'E251_ImageSearchUrl') as
                | string
                | undefined,
            e251_ImageGenerationPrompt: pick(
                'e251_ImageGenerationPrompt',
                'E251_ImageGenerationPrompt'
            ) as string | undefined,
            e251_RasterOptions: mapWireRasterOptions(
                pick('e251_RasterOptions', 'E251_RasterOptions')
            ),
        };
    });
}

function mapWireStepVideo(raw: unknown): StepVideoDto | undefined {
    if (raw == null || typeof raw !== 'object') return undefined;
    const r = raw as Record<string, unknown>;
    const videoApproved = pickWireField(
        r,
        'e251_StepVideoApproved',
        'E251_StepVideoApproved',
        'e251_VideoApproved',
        'E251_VideoApproved'
    );
    const title = pickWireField(
        r,
        'e251_StepVideoTitle',
        'E251_StepVideoTitle',
        'e251_Title',
        'E251_Title'
    ) as string | undefined;
    const content = pickWireField(
        r,
        'e251_StepVideoContent',
        'E251_StepVideoContent',
        'e251_Content',
        'E251_Content'
    ) as string | undefined;
    const url = pickWireField(
        r,
        'e251_StepVideoUrl',
        'E251_StepVideoUrl',
        'e251_Url',
        'E251_Url'
    ) as string | undefined;
    const options = mapWireVideoOptions(
        pickWireField(r, 'e251_StepVideoOptions', 'E251_StepVideoOptions', 'e251_VideoOptions', 'E251_VideoOptions')
    );
    const removedFromYouTube = pickWireField(
        r,
        'e251_VideoRemovedFromYouTube',
        'E251_VideoRemovedFromYouTube'
    );
    const isRemoved =
        removedFromYouTube === true || removedFromYouTube === 'true';
    if (!title?.trim() && !content?.trim() && !url?.trim() && !options?.length && !isRemoved) return undefined;
    return {
        e251_StepVideoTitle: title,
        e251_VideoSearchPurpose: pickWireField(
            r,
            'e251_VideoSearchPurpose',
            'E251_VideoSearchPurpose',
            'e251_SearchPurpose',
            'E251_SearchPurpose'
        ) as string | undefined,
        e251_StepVideoContent: content,
        e251_StepVideoUrl: url,
        e251_StepVideoChannelTitle: pickWireField(
            r,
            'e251_StepVideoChannelTitle',
            'E251_StepVideoChannelTitle',
            'e251_VideoChannelTitle',
            'E251_VideoChannelTitle'
        ) as string | undefined,
        e251_StepVideoOptions: options,
        e251_SelectedVideoId: pickWireField(
            r,
            'e251_SelectedVideoId',
            'E251_SelectedVideoId'
        ) as string | undefined,
        e251_StepVideoApproved:
            videoApproved === true || videoApproved === 'true'
                ? true
                : videoApproved === false || videoApproved === 'false'
                  ? false
                  : undefined,
        e251_VideoRemovedFromYouTube: isRemoved ? true : undefined
    };
}

function mapWireInstructionStepList(raw: unknown): NonNullable<LessonDetailDto['e251_InstructionSteps']> {
    if (!Array.isArray(raw)) return [];
    return raw.map(row => {
        const r = row as Record<string, unknown>;
        const pick = (a: string, b: string) => pickWireField(r, a, b);
        const stepNumber = pick('e251_StepNumber', 'E251_StepNumber');
        const stepId = pick('e251_StepId', 'E251_StepId');
        const durationRaw = pick('e251_DurationMinutes', 'E251_DurationMinutes');
        const sessionRaw = pickWireField(
            r,
            'e251_StepSessionId',
            'E251_StepSessionId',
            'e251_SessionId',
            'E251_SessionId'
        );
        const completedAt = pickWireField(
            r,
            'e251_StepCompletedAt',
            'E251_StepCompletedAt',
            'e251_CompletedAt',
            'E251_CompletedAt'
        );
        const isCompleted = pick('e251_IsCompleted', 'E251_IsCompleted');
        return {
            e251_StepId: stepId != null && String(stepId) !== '' ? String(stepId) : undefined,
            e251_StepNumber: stepNumber == null || stepNumber === '' ? undefined : Number(stepNumber),
            e251_Summary: pick('e251_Summary', 'E251_Summary') as string | undefined,
            e251_DurationMinutes:
                durationRaw == null || durationRaw === '' ? undefined : Number(durationRaw),
            e251_DetailedInstructions: pick('e251_DetailedInstructions', 'E251_DetailedInstructions') as
                | string
                | undefined,
            e251_IsActive: (() => {
                const active = pick('e251_IsActive', 'E251_IsActive');
                if (active === true || active === 'true') return true;
                if (active === false || active === 'false') return false;
                return undefined;
            })(),
            e251_IsCompleted:
                isCompleted === true || isCompleted === 'true'
                    ? true
                    : isCompleted === false || isCompleted === 'false'
                      ? false
                      : undefined,
            e251_StepCompletedAt:
                completedAt == null || completedAt === ''
                    ? undefined
                    : completedAt === null
                      ? null
                      : String(completedAt),
            e251_StepKind: pickEnumerationKey(pick('e251_StepKind', 'E251_StepKind')),
            e251_StepSessionId:
                sessionRaw == null || sessionRaw === '' ? undefined : Number(sessionRaw),
            e251_TeachingMethod: pick('e251_TeachingMethod', 'E251_TeachingMethod') as string | undefined,
            e251_TeachingMethodDescription: pick(
                'e251_TeachingMethodDescription',
                'E251_TeachingMethodDescription'
            ) as string | undefined,
            e251_StepMaterials: mapWireStepMaterials(pick('e251_StepMaterials', 'E251_StepMaterials')),
            e251_Reading: mapWireStepReading(pick('e251_Reading', 'E251_Reading')),
            e251_WorksheetSection: mapWireStepWorksheetSection(
                pick('e251_WorksheetSection', 'E251_WorksheetSection')
            ),
            e251_Video: mapWireStepVideo(pick('e251_Video', 'E251_Video')),
            e251_RasterFigures: mapWireRasterFigures(pick('e251_RasterFigures', 'E251_RasterFigures'))
        };
    });
}

export function mergeInstructionStepsById(
    existing: InstructionStepDto[] | undefined,
    incoming: InstructionStepDto[]
): InstructionStepDto[] {
    if (!incoming.length) return existing ?? [];
    const incomingById = new Map<string, InstructionStepDto>();
    for (const step of incoming) {
        const id = step.e251_StepId;
        if (id) incomingById.set(String(id).toLowerCase(), step);
    }
    if (!existing?.length) return [...incoming];

    const seen = new Set<string>();
    const merged = existing.map(step => {
        const id = step.e251_StepId;
        if (!id) return step;
        const key = String(id).toLowerCase();
        seen.add(key);
        const patch = incomingById.get(key);
        return patch ? { ...step, ...patch } : step;
    });

    for (const step of incoming) {
        const id = step.e251_StepId;
        if (!id) continue;
        const key = String(id).toLowerCase();
        if (!seen.has(key)) merged.push(step);
    }

    return merged;
}

function mapWireVideoOptions(raw: unknown): SupportingMaterialDto['e251_VideoOptions'] {
    if (!Array.isArray(raw)) return undefined;
    return raw.map(row => {
        const r = row as Record<string, unknown>;
        const pick = (a: string, b: string) => pickWireField(r, a, b);
        const rank = pick('e251_VideoOptionRank', 'E251_VideoOptionRank');
        const embeddable = pick('e251_VideoEmbeddable', 'E251_VideoEmbeddable');
        return {
            e251_VideoId: pick('e251_VideoId', 'E251_VideoId') as string | undefined,
            e251_VideoTitle: pick('e251_VideoTitle', 'E251_VideoTitle') as string | undefined,
            e251_VideoUrl: pick('e251_VideoUrl', 'E251_VideoUrl') as string | undefined,
            e251_VideoChannelTitle: pick('e251_VideoChannelTitle', 'E251_VideoChannelTitle') as string | undefined,
            e251_VideoOptionRank: rank == null || rank === '' ? undefined : Number(rank),
            e251_VideoEmbeddable:
                embeddable === true || embeddable === 'true'
                    ? true
                    : embeddable === false || embeddable === 'false'
                      ? false
                      : undefined
        };
    });
}

/**
 * When GetUnits returns a stale snapshot (e.g. concurrent C322/C323 on the same unit),
 * keep video materials that were already approved in client state.
 */
export function mergePreservedVideoApprovals<T extends UnitDto>(prevUnits: T[], incomingUnits: T[]): T[] {
    if (!prevUnits.length) return incomingUnits;

    const approvedKeys = new Set<string>();
    for (const unit of prevUnits) {
        for (const lesson of unit.e251_Lessons ?? []) {
            for (const step of lesson.e251_InstructionSteps ?? []) {
                if (step.e251_Video?.e251_StepVideoApproved === true && step.e251_StepId) {
                    approvedKeys.add(
                        videoApprovalStateKey(unit.e251_UnitId, lesson.e251_LessonId, step.e251_StepId)
                    );
                }
            }
        }
    }

    if (approvedKeys.size === 0) return incomingUnits;

    let anyChange = false;
    const next = incomingUnits.map(unit => {
        let unitChanged = false;
        const lessons = unit.e251_Lessons?.map(lesson => {
            let lessonChanged = false;

            const steps = lesson.e251_InstructionSteps?.map(step => {
                if (!step.e251_Video || !step.e251_StepId || step.e251_Video.e251_StepVideoApproved === true) {
                    return step;
                }
                const key = videoApprovalStateKey(
                    unit.e251_UnitId,
                    lesson.e251_LessonId,
                    step.e251_StepId
                );
                if (!approvedKeys.has(key)) return step;
                lessonChanged = true;
                unitChanged = true;
                anyChange = true;
                return {
                    ...step,
                    e251_Video: { ...step.e251_Video, e251_StepVideoApproved: true }
                };
            });

            if (!lessonChanged) return lesson;
            return {
                ...lesson,
                ...(steps ? { e251_InstructionSteps: steps } : {})
            };
        });
        return unitChanged ? { ...unit, e251_Lessons: lessons } : unit;
    });

    return anyChange ? next : incomingUnits;
}

export function rasterApprovalStateKey(
    unitId: unknown,
    lessonId: unknown,
    stepId: unknown,
    figureKey: unknown
): string {
    return `${normalizeIdPart(unitId)}|${normalizeIdPart(lessonId)}|${normalizeIdPart(stepId)}|${normalizeIdPart(figureKey)}`;
}

export function applyLessonRasterFigureReadyToUnits(
    prevUnits: UnitDto[],
    unitIdWire: unknown,
    lessonIdWire: unknown,
    stepIdWire: unknown,
    figureKeyWire: unknown,
    patch: Partial<StepRasterFigureDto>
): UnitDto[] {
    const unitKey = normalizeIdPart(unitIdWire);
    const lessonKey = normalizeIdPart(lessonIdWire);
    const stepKey = normalizeIdPart(stepIdWire);
    const figureKey = normalizeIdPart(figureKeyWire);
    let anyChange = false;
    const next = prevUnits.map(unit => {
        if (normalizeIdPart(unit.e251_UnitId) !== unitKey) return unit;
        const lessons = unit.e251_Lessons;
        if (!lessons?.length) return unit;
        let unitChanged = false;
        const newLessons = lessons.map(lesson => {
            if (normalizeIdPart(lesson.e251_LessonId) !== lessonKey) return lesson;
            let lessonChanged = false;
            const newSteps = lesson.e251_InstructionSteps?.map(step => {
                if (normalizeIdPart(step.e251_StepId) !== stepKey) return step;
                const figures = step.e251_RasterFigures;
                if (!figures?.length) return step;
                let stepChanged = false;
                const newFigures = figures.map(figure => {
                    if (normalizeIdPart(figure.e251_FigureKey) !== figureKey) return figure;
                    stepChanged = true;
                    lessonChanged = true;
                    anyChange = true;
                    return { ...figure, ...patch, e251_RasterApproved: false };
                });
                if (!stepChanged) return step;
                return { ...step, e251_RasterFigures: newFigures };
            });
            if (!lessonChanged) return lesson;
            unitChanged = true;
            return { ...lesson, e251_InstructionSteps: newSteps };
        });
        return unitChanged ? { ...unit, e251_Lessons: newLessons } : unit;
    });
    return anyChange ? next : prevUnits;
}

export function applyLessonRasterFigureApprovedToUnits(
    prevUnits: UnitDto[],
    unitIdWire: unknown,
    lessonIdWire: unknown,
    materialIdWire: unknown,
    figureKeyWire: unknown
): UnitDto[] {
    const unitKey = normalizeIdPart(unitIdWire);
    const lessonKey = normalizeIdPart(lessonIdWire);
    const stepKey = normalizeIdPart(materialIdWire);
    const figureKey = normalizeIdPart(figureKeyWire);
    let anyChange = false;
    const next = prevUnits.map(unit => {
        if (normalizeIdPart(unit.e251_UnitId) !== unitKey) return unit;
        const lessons = unit.e251_Lessons;
        if (!lessons?.length) return unit;
        let unitChanged = false;
        const newLessons = lessons.map(lesson => {
            if (normalizeIdPart(lesson.e251_LessonId) !== lessonKey) return lesson;
            let lessonChanged = false;
            const newSteps = lesson.e251_InstructionSteps?.map(step => {
                if (normalizeIdPart(step.e251_StepId) !== stepKey) return step;
                const figures = step.e251_RasterFigures;
                if (!figures?.length) return step;
                let stepChanged = false;
                const newFigures = figures.map(figure => {
                    if (normalizeIdPart(figure.e251_FigureKey) !== figureKey) return figure;
                    if (figure.e251_RasterApproved === true) return figure;
                    stepChanged = true;
                    lessonChanged = true;
                    anyChange = true;
                    return { ...figure, e251_RasterApproved: true };
                });
                if (!stepChanged) return step;
                return { ...step, e251_RasterFigures: newFigures };
            });
            if (!lessonChanged) return lesson;
            unitChanged = true;
            return { ...lesson, e251_InstructionSteps: newSteps };
        });
        return unitChanged ? { ...unit, e251_Lessons: newLessons } : unit;
    });
    return anyChange ? next : prevUnits;
}

export function applyLessonFigureTextOnlyAcceptedToUnits(
    prevUnits: UnitDto[],
    unitIdWire: unknown,
    lessonIdWire: unknown,
    materialIdWire: unknown,
    figureKeyWire: unknown,
    materialContentWire: unknown,
    isRasterWire: unknown
): UnitDto[] {
    const unitKey = normalizeIdPart(unitIdWire);
    const lessonKey = normalizeIdPart(lessonIdWire);
    const stepKey = normalizeIdPart(materialIdWire);
    const figureKey = normalizeIdPart(figureKeyWire);
    const materialContent =
        materialContentWire == null ? undefined : String(materialContentWire);
    const isRaster = isRasterWire === true || isRasterWire === 'true';
    let anyChange = false;
    const next = prevUnits.map(unit => {
        if (normalizeIdPart(unit.e251_UnitId) !== unitKey) return unit;
        const lessons = unit.e251_Lessons;
        if (!lessons?.length) return unit;
        let unitChanged = false;
        const newLessons = lessons.map(lesson => {
            if (normalizeIdPart(lesson.e251_LessonId) !== lessonKey) return lesson;
            let lessonChanged = false;
            const newSteps = lesson.e251_InstructionSteps?.map(step => {
                if (normalizeIdPart(step.e251_StepId) !== stepKey) return step;
                let stepChanged = false;
                let nextStep = step;

                if (isRaster) {
                    const figures = step.e251_RasterFigures;
                    if (!figures?.length) return step;
                    const newFigures = figures.map(figure => {
                        if (normalizeIdPart(figure.e251_FigureKey) !== figureKey) return figure;
                        stepChanged = true;
                        return {
                            ...figure,
                            e251_GenerationState: 'ready',
                            e251_GenerationError: undefined,
                            e251_TextOnlyAccepted: true,
                            e251_RasterApproved: true,
                        };
                    });
                    if (stepChanged) nextStep = { ...step, e251_RasterFigures: newFigures };
                }

                if (materialContent != null) {
                    const contentFigureKey = normalizeFigureKey(String(figureKeyWire ?? ''));
                    const readingHosts = contentHostsFigureKey(
                        step.e251_Reading?.e251_ReadingContent,
                        contentFigureKey
                    );
                    const worksheetHosts = contentHostsFigureKey(
                        step.e251_WorksheetSection?.e251_WorksheetContent,
                        contentFigureKey
                    );
                    if (readingHosts && step.e251_Reading) {
                        stepChanged = true;
                        nextStep = {
                            ...nextStep,
                            e251_Reading: {
                                ...step.e251_Reading,
                                e251_ReadingContent: materialContent,
                            },
                        };
                    } else if (worksheetHosts && step.e251_WorksheetSection) {
                        stepChanged = true;
                        nextStep = {
                            ...nextStep,
                            e251_WorksheetSection: {
                                ...step.e251_WorksheetSection,
                                e251_WorksheetContent: materialContent,
                            },
                        };
                    }
                }

                if (!stepChanged) return step;
                lessonChanged = true;
                anyChange = true;
                return nextStep;
            });
            if (!lessonChanged) return lesson;
            unitChanged = true;
            return { ...lesson, e251_InstructionSteps: newSteps };
        });
        return unitChanged ? { ...unit, e251_Lessons: newLessons } : unit;
    });
    return anyChange ? next : prevUnits;
}

export function applyLessonVideoSelectionChangedToUnits(
    prevUnits: UnitDto[],
    unitIdWire: unknown,
    lessonIdWire: unknown,
    stepIdWire: unknown,
    videoIdWire: string | null
): UnitDto[] {
    const unitKey = normalizeIdPart(unitIdWire);
    const lessonKey = normalizeIdPart(lessonIdWire);
    const stepKey = normalizeIdPart(stepIdWire);
    let anyChange = false;
    const next = prevUnits.map(unit => {
        if (normalizeIdPart(unit.e251_UnitId) !== unitKey) return unit;
        const lessons = unit.e251_Lessons;
        if (!lessons?.length) return unit;
        let unitChanged = false;
        const newLessons = lessons.map(lesson => {
            if (normalizeIdPart(lesson.e251_LessonId) !== lessonKey) return lesson;
            let lessonChanged = false;

            const newSteps = lesson.e251_InstructionSteps?.map(step => {
                if (normalizeIdPart(step.e251_StepId) !== stepKey || !step.e251_Video) return step;
                const nextVideoId = videoIdWire == null || videoIdWire === '' ? undefined : String(videoIdWire);
                const selectionChanged =
                    (step.e251_Video.e251_SelectedVideoId ?? undefined) !== nextVideoId;
                const needsUnapprove = step.e251_Video.e251_StepVideoApproved === true;
                if (!selectionChanged && !needsUnapprove) return step;
                lessonChanged = true;
                anyChange = true;
                return {
                    ...step,
                    e251_Video: {
                        ...step.e251_Video,
                        e251_SelectedVideoId: nextVideoId ?? null,
                        e251_StepVideoApproved: false
                    }
                };
            });

            if (!lessonChanged) return lesson;
            unitChanged = true;
            return {
                ...lesson,
                ...(newSteps ? { e251_InstructionSteps: newSteps } : {})
            };
        });
        return unitChanged ? { ...unit, e251_Lessons: newLessons } : unit;
    });
    return anyChange ? next : prevUnits;
}

export function mapEventRasterOptions(raw: unknown): RasterOptionDto[] | undefined {
    return mapWireRasterOptions(raw);
}

export function applyLessonVideoApprovedToUnits(
    prevUnits: UnitDto[],
    unitIdWire: unknown,
    lessonIdWire: unknown,
    materialIdWire: unknown
): UnitDto[] {
    const unitKey = normalizeIdPart(unitIdWire);
    const lessonKey = normalizeIdPart(lessonIdWire);
    const materialKey = normalizeIdPart(materialIdWire);
    let anyChange = false;
    const next = prevUnits.map(unit => {
        if (normalizeIdPart(unit.e251_UnitId) !== unitKey) return unit;
        const lessons = unit.e251_Lessons;
        if (!lessons?.length) return unit;
        let unitChanged = false;
        const newLessons = lessons.map(lesson => {
            if (normalizeIdPart(lesson.e251_LessonId) !== lessonKey) return lesson;
            let lessonChanged = false;

            const newSteps = lesson.e251_InstructionSteps?.map(step => {
                if (normalizeIdPart(step.e251_StepId) !== materialKey || !step.e251_Video) return step;
                if (step.e251_Video.e251_StepVideoApproved === true) return step;
                lessonChanged = true;
                anyChange = true;
                return {
                    ...step,
                    e251_Video: { ...step.e251_Video, e251_StepVideoApproved: true }
                };
            });

            if (!lessonChanged) return lesson;
            unitChanged = true;
            return {
                ...lesson,
                ...(newSteps ? { e251_InstructionSteps: newSteps } : {})
            };
        });
        return unitChanged ? { ...unit, e251_Lessons: newLessons } : unit;
    });
    return anyChange ? next : prevUnits;
}

export function applyLessonRemovedFromUnits(
    prevUnits: UnitDto[],
    unitIdWire: unknown,
    lessonIdWire: unknown
): UnitDto[] {
    const unitKey = normalizeIdPart(unitIdWire);
    const lessonKey = normalizeIdPart(lessonIdWire);
    let anyChange = false;
    const next = prevUnits.map(unit => {
        if (normalizeIdPart(unit.e251_UnitId) !== unitKey) return unit;
        const lessons = unit.e251_Lessons;
        if (!lessons?.length) return unit;
        const filtered = lessons.filter(
            lesson => normalizeIdPart(lesson.e251_LessonId) !== lessonKey
        );
        if (filtered.length === lessons.length) return unit;
        anyChange = true;
        return { ...unit, e251_Lessons: filtered };
    });
    return anyChange ? next : prevUnits;
}

export function applyLessonPatchToUnits(
    prevUnits: UnitDto[],
    unitIdWire: unknown,
    lessonIdWire: unknown,
    patch: Partial<LessonDetailDto>,
    options?: { mergeInstructionSteps?: boolean }
): UnitDto[] {
    const mergeInstructionSteps = options?.mergeInstructionSteps !== false;
    if (Object.keys(patch).length === 0) return prevUnits;
    const unitKey = normalizeIdPart(unitIdWire);
    const lessonKey = normalizeIdPart(lessonIdWire);
    let anyChange = false;
    const next = prevUnits.map(unit => {
        if (normalizeIdPart(unit.e251_UnitId) !== unitKey) return unit;
        const lessons = unit.e251_Lessons;
        if (!lessons?.length) return unit;
        let unitChanged = false;
        const newLessons = lessons.map(lesson => {
            if (normalizeIdPart(lesson.e251_LessonId) !== lessonKey) return lesson;
            unitChanged = true;
            anyChange = true;
            const mergedPatch = { ...patch };
            if (patch.e251_InstructionSteps !== undefined) {
                mergedPatch.e251_InstructionSteps = mergeInstructionSteps
                    ? mergeInstructionStepsById(
                          lesson.e251_InstructionSteps,
                          patch.e251_InstructionSteps
                      )
                    : patch.e251_InstructionSteps;
            }
            return { ...lesson, ...mergedPatch };
        });
        return unitChanged ? { ...unit, e251_Lessons: newLessons } : unit;
    });
    return anyChange ? next : prevUnits;
}

export function buildPatchFromE258(event: Record<string, unknown>): Partial<LessonDetailDto> {
    const patch: Partial<LessonDetailDto> = {};
    const state = pickEnumerationKey(event.e258_LessonState ?? event.E258_LessonState);
    if (state != null) {
        patch.e251_LessonState = state;
    }
    const stepsRaw = event.e258_InstructionSteps ?? event.E258_InstructionSteps;
    if (stepsRaw !== undefined) {
        patch.e251_InstructionSteps = mapWireInstructionStepList(stepsRaw);
    }
    return patch;
}

export function buildPatchFromE318(event: Record<string, unknown>): Partial<LessonDetailDto> {
    const patch: Partial<LessonDetailDto> = {};
    const state = pickEnumerationKey(event.e318_LessonState ?? event.E318_LessonState);
    if (state != null) {
        patch.e251_LessonState = state;
        if (state === '50') {
            patch.e251_ActiveExternalSessionId = null;
            patch.e251_LessonCompletionDate = new Date().toISOString();
        }
    }
    const stepsRaw = event.e318_InstructionSteps ?? event.E318_InstructionSteps;
    if (stepsRaw !== undefined) {
        patch.e251_InstructionSteps = mapWireInstructionStepList(stepsRaw);
    }
    return patch;
}

export function buildPatchFromE326(event: Record<string, unknown>): Partial<LessonDetailDto> {
    const patch: Partial<LessonDetailDto> = {};
    const title = event.e326_LessonTitle ?? event.E326_LessonTitle;
    if (title) patch.e251_LessonTitle = String(title);
    const summary = event.e326_LessonSummary ?? event.E326_LessonSummary;
    if (summary) patch.e251_LessonSummary = String(summary);
    const duration = event.e326_LessonDuration ?? event.E326_LessonDuration;
    if (duration != null && duration !== '') {
        patch.e251_LessonDuration = Number(duration);
    }
    const mats = event.e326_LessonMaterials ?? event.E326_LessonMaterials;
    if (mats) patch.e251_LessonMaterials = String(mats);
    const stepsRaw = event.e326_InstructionSteps ?? event.E326_InstructionSteps;
    if (stepsRaw !== undefined) {
        patch.e251_InstructionSteps = mapWireInstructionStepList(stepsRaw);
    }
    const priorGuidanceRaw = event.e326_PriorDeliveryGuidance ?? event.E326_PriorDeliveryGuidance;
    if (priorGuidanceRaw !== undefined) {
        patch.e251_PriorDeliveryGuidance = mapWirePriorDeliveryGuidanceList(priorGuidanceRaw);
    }
    return patch;
}

export function buildPatchFromE261(event: Record<string, unknown>): Partial<LessonDetailDto> {
    const patch: Partial<LessonDetailDto> = {};
    const state = pickEnumerationKey(event.e261_LessonState ?? event.E261_LessonState);
    if (state != null) {
        patch.e251_LessonState = state;
    }
    const cd = event.e261_CompletionDate ?? event.E261_CompletionDate;
    if (cd !== undefined) {
        patch.e251_LessonCompletionDate = cd == null ? null : String(cd);
    }
    const how = event.e261_HowDidLessonGo ?? event.E261_HowDidLessonGo;
    if (how !== undefined) {
        patch.e251_HowDidLessonGo = how == null ? undefined : String(how);
    }
    const imp = event.e261_WhatCouldBeImproved ?? event.E261_WhatCouldBeImproved;
    if (imp !== undefined) {
        patch.e251_WhatCouldBeImproved = imp == null ? undefined : String(imp);
    }
    return patch;
}

export function mapWireInstructionStepsFromEvent(raw: unknown): NonNullable<LessonDetailDto['e251_InstructionSteps']> {
    return mapWireInstructionStepList(raw);
}
